<!--
  RatesTab.vue
  콜봇 월 정산 · 병원 상세 요율 탭(`tab=rates`) — 화면 명세 2-1 "요율 패널".

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2 요율 탭·2-1", "요율", "요청 계약 3-1·3-2·3-3"
  - 이 달의 유효 요율 표(SettlementRateCard mode 'effective' — 기본 요율 화면과 같은 표, 2026-10-27 목업): 이 달 기본 버전에
    이 회사 예외 칸을 덮은 값. 항목마다 기본/예외 배지, 예외 항목 아래 그 달 기본값, 이 달 쓴 항목 강조와 그 양, 쓰였는데
    판매가 없는 항목 경고색.
    확정·검토 필요 달은 정산 스냅샷 값이라 "마감 시점 요율" 배지를 단다(카드 몫).
  - 카드 위 안내(한 문장): 고친 요율이 이 달에 언제 들어가는지(추정 중 바로 / 검토 필요 [다시 계산] 뒤 / 확정 안 바뀜).
    그 달 기본 요율이 없으면(`rates` null, DEFAULT_PLAN_MISSING) 기본 요율 화면으로 가는 안내 — 그 달 기본 버전이
    요금표 v70 이전 모양이면(`details.legacy_version`) 그 버전을 새 칸으로 다시 저장하라고 한다.
  - 요금표 v70(~/md/front/admin-콜봇-월정산-call-api-구현회신.md "2026-10-16 개정" 3·8): 덮을 칸은 모두
    `{ value, source, default_value? }` 다. v70 이전에 만든 정산서(`statement.legacy`)는 `rates` 가 예전 모양이라 카드는
    칸 대신 안내를 보이고(카드 몫), 위에서 [다시 계산](확정이면 [재수정] 먼저)으로 이끈다.
  - 회사 예외: `[예외 추가/수정]`(PlanOverrideModal)과 예외 이력(OverrideHistory — 버전 삭제 포함).
    기본 요율 편집은 여기서 하지 않는다 — `기본 요율 화면 →`(화면 명세 3).

  데이터
  - 정산 상세(셸이 부른다): `rates`(칸별 값·source·default_value·default_version·override_version),
    `charge.lines`·`usage`(이 달 쓴 칸과 그 양 — settlementRules.cellUsageNotes), `warnings`(판매가 미정 회선·
    DEFAULT_PLAN_MISSING legacy_version), `statement.status`·`statement.legacy`.
  - 탭을 처음 열 때(onMounted) 회사 예외(3-2 GET .../plan-overrides — 이력·cost_rates)와 기본 요율
    (3-1 GET .../default-plans)을 같이 부른다. 기본 요율은 예외 칸 옆 그 달 기본값과 모달의 "기본 따름" 값에
    쓴다(상세 `rates` 에는 덮인 칸의 기본값이 없다). 받는 동안 카드는 `rates` 만으로 그린다.
  - 쓰기(예외 저장·전부 해제·버전 삭제) 뒤에는 예외 목록과 정산 상세(reload)를 다시 부른다. 그 쓰기가 이 달에
    쓰는 버전을 바꿨으면 검토 필요 달은 정산 탭 [다시 계산]으로, 확정 달은 재수정으로 이끈다.

  셸 ↔ 탭(고정): props companyId · month · detail · detailLoading · api · mock · reload · intent,
  emits go-tab · open-reopen · intent-done. intent 'open-override-modal' 은 목록을 받은 뒤 모달을 연다.
-->
<template>
  <div class="rates-tab">
    <!-- 그 달 기본 요율 없음 — 유효 요율도 금액도 없다 -->
    <a-alert v-if="!rates" type="error" show-icon>
      <template #message>
        {{ planMissing.message }}<span class="rates-tab__code">{{ planMissing.code }}</span>
      </template>
      <template #description>
        {{ planMissing.description }}
        <router-link :to="ratesPageTo" class="rates-tab__link">기본 요율 화면 →</router-link>
      </template>
    </a-alert>

    <!-- 이 카드가 지금 값인지 마감 시점 값인지, 고친 뒤 이 달에 넣으려면 무엇을 하는지 -->
    <a-alert
      v-else-if="statusNotice"
      :type="statusNotice.type"
      show-icon
      :message="statusNotice.message"
      :description="statusNotice.description || undefined"
    >
      <template v-if="statusNotice.action" #action>
        <a-button size="small" @click="runNoticeAction(statusNotice.action)">
          {{ NOTICE_ACTION_LABELS[statusNotice.action] }}
        </a-button>
      </template>
    </a-alert>

    <a-spin :spinning="detailLoading">
      <SettlementRateCard
        mode="effective"
        :rates="rates"
        :cost-rates="costRates"
        :default-rates="cardDefaultPlan"
        :usage-notes="usageNotes"
        :warnings="warnings"
        :legacy="legacyStatement"
        :snapshot="isSnapshot"
      >
        <template #header-extra>
          <router-link :to="ratesPageTo" class="rates-tab__link">기본 요율 화면 →</router-link>
          <a-tooltip :title="editBlockedReason || undefined">
            <a-button type="primary" class="yellow-button" :disabled="!!editBlockedReason" @click="openModal">
              예외 추가/수정
            </a-button>
          </a-tooltip>
        </template>
      </SettlementRateCard>
    </a-spin>

    <!-- 회사 예외 — 이 달 적용 버전, 할 일 안내, 이력 -->
    <section class="rates-tab__panel" :aria-labelledby="panelTitleId">
      <header class="rates-tab__panel-head">
        <h3 :id="panelTitleId" class="rates-tab__panel-title">회사 예외</h3>
        <span v-if="appliedSummary" class="rates-tab__pill" :class="{ 'is-override': appliedCellCount > 0 }">
          {{ appliedSummary }}
        </span>
      </header>

      <p class="rates-tab__note is-muted" :title="OVERRIDE_RULE_TITLE">
        요율이 기본과 다른 칸만 덮고, 덮지 않은 칸은 기본 요율을 따릅니다.
      </p>
      <p v-if="snapshotGapText" class="rates-tab__note is-gap">{{ snapshotGapText }}</p>

      <a-alert v-if="plansError" type="error" show-icon :message="plansError">
        <template #action>
          <a-button size="small" :loading="plansLoading" @click="loadPlans">다시 시도</a-button>
        </template>
      </a-alert>

      <OverrideHistory
        :items="overrides.items"
        :cost-rates="costRates"
        :applied-version="appliedVersion"
        :loading="overrides.loading && !overrides.loaded"
        :failed="!!overrides.error"
        :api="api"
        :company-id="companyId"
        @deleted="onDeleted"
        @refresh="loadOverrides"
      />
    </section>

    <PlanOverrideModal
      :open="modalOpen"
      :company-id="companyId"
      :company-name="companyName"
      :month="month"
      :overrides="overrides.items"
      :default-plans="defaults.items"
      :cost-rates="costRates"
      :api="api"
      @close="modalOpen = false"
      @saved="onSaved"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { message } from 'ant-design-vue';

import SettlementRateCard from '../SettlementRateCard.vue';
import OverrideHistory from './OverrideHistory.vue';
import PlanOverrideModal from './PlanOverrideModal.vue';
import { monthLabel } from '../../service/settlementFormat';
import {
  CLOSING_TEXT,
  SETTLEMENT_ROUTE_NAMES,
  cellUsageNotes,
  effectiveDefaultPlan,
  effectiveOverride,
  isLegacyStatementDetail,
  legacyPlanVersionOf,
  legacyStatementActionText,
  overrideBodyCellKeys,
  overriddenCellKeys,
  statementStatusOf,
} from '../../service/settlementRules';
import { parseCallApiError } from '@/views/Callbot/service/callApiError';

const props = defineProps({
  companyId: { type: String, required: true },
  /** 'YYYY-MM'(KST) */
  month: { type: String, required: true },
  /** 정산 상세 응답(요청 계약 2) — 셸이 받은 뒤에만 이 탭을 마운트한다(처음 로드·실패는 셸 몫) */
  detail: { type: Object, required: true },
  detailLoading: { type: Boolean, default: false },
  /** settlementApi 또는 목(getSettlementApi()) */
  api: { type: Object, required: true },
  /** 목 데이터 여부 — 이 탭은 따로 쓰지 않는다(목 배지·조작은 셸이 그린다) */
  mock: { type: Boolean, default: false },
  /** 정산 상세를 다시 받는다 — 쓰기 뒤마다 부른다 */
  reload: { type: Function, required: true },
  /** 이 탭에 온 지시 { type, payload?, nonce } — 'open-override-modal' */
  intent: { type: Object, default: null },
});

const emit = defineEmits(['go-tab', 'open-reopen', 'intent-done']);

const UNAVAILABLE_TEXT = '회사 예외·기본 요율 API 가 아직 call-api 에 배포되지 않았습니다.';
/** 쓰기 뒤 알림의 앞 문장 */
const WRITE_DONE_TEXT = {
  saved: '예외를 저장했습니다.',
  cleared: '예외를 전부 해제했습니다.',
  deleted: '예외 버전을 삭제했습니다.',
};
const NOTICE_ACTION_LABELS = { recalculate: '정산 탭에서 다시 계산', reopen: '재수정' };
/** 회사 예외 규칙의 나머지(툴팁) */
const OVERRIDE_RULE_TITLE =
  '덮지 않은 칸은 기본 요율 새 버전을 따라갑니다. 예외 버전끼리는 쌓이지 않아 그 달에는 적용 시작 월이 가장 최근인 버전 하나만 씁니다.';

const panelTitleId = `st-rates-tab-overrides-${getCurrentInstance()?.uid ?? 0}`;

// ---------------------------------------------------------------------------
// 회사 예외·기본 요율 목록 — 탭을 처음 열 때와 쓰기 뒤에 받는다
// ---------------------------------------------------------------------------

const listState = () => ({ items: [], costRates: null, loading: true, loaded: false, error: '' });
const overrides = reactive(listState());
const defaults = reactive(listState());

/** 목록 하나를 받는다. 다시 받는 동안에는 이전 목록을 그대로 보인다(깜빡이지 않게) */
async function loadList(state, fetcher, fallback) {
  state.loading = true;
  state.error = '';
  try {
    const res = await fetcher();
    state.items = Array.isArray(res?.items) ? res.items : [];
    state.costRates = res?.cost_rates ?? null;
    state.loaded = true;
  } catch (err) {
    state.error = parseCallApiError(err, fallback, { unavailableMessage: UNAVAILABLE_TEXT });
  } finally {
    state.loading = false;
  }
}

const loadOverrides = () =>
  loadList(overrides, () => props.api.listPlanOverrides(props.companyId), '회사 예외를 불러오지 못했습니다.');
const loadDefaults = () => loadList(defaults, () => props.api.listDefaultPlans(), '기본 요율을 불러오지 못했습니다.');

async function loadPlans() {
  await Promise.all([loadOverrides(), loadDefaults()]);
  settlePendingIntent();
}

// 셸이 회사+달마다 이 탭을 한 번만 마운트한다 — 탭을 다시 열 때는 부르지 않는다
onMounted(loadPlans);

const plansLoading = computed(() => overrides.loading || defaults.loading);
// 예외 버전을 고치면 그 버전을 통째로 바꾸므로, 목록을 모르는 채로는 모달을 열지 않는다
const plansReady = computed(() => overrides.loaded && defaults.loaded && !overrides.error && !defaults.error);
const plansError = computed(() => [...new Set([overrides.error, defaults.error].filter(Boolean))].join(' '));
const editBlockedReason = computed(() => {
  if (plansReady.value) return '';
  return plansLoading.value
    ? '예외 이력과 기본 요율을 불러오는 중입니다.'
    : '예외 이력과 기본 요율을 불러오지 못해 열 수 없습니다. 아래 회사 예외에서 다시 시도하세요.';
});

// ---------------------------------------------------------------------------
// 카드
// ---------------------------------------------------------------------------

const rates = computed(() => props.detail.rates ?? null);
const status = computed(() => statementStatusOf(props.detail));
// 확정·검토 필요 달의 `rates` 는 정산 스냅샷 값이다(요청 계약 2)
const isSnapshot = computed(() => status.value === 'confirmed' || status.value === 'review');
const warnings = computed(() => (Array.isArray(props.detail.warnings) ? props.detail.warnings : []));
/** 이 달 쓴 칸과 그 양 — v70 이전 정산서는 사용량이 예전 모양이라 읽지 않는다 */
const legacyStatement = computed(() => isLegacyStatementDetail(props.detail));
const usageNotes = computed(() => (legacyStatement.value ? {} : cellUsageNotes(props.detail)));
/** 그 달 기본 버전이 v70 이전 모양이면 그 버전(DEFAULT_PLAN_MISSING `legacy_version`) */
const legacyVersion = computed(() => legacyPlanVersionOf(warnings.value));
const costRates = computed(() => overrides.costRates ?? defaults.costRates ?? null);
const companyName = computed(() => props.detail.company_name || '');

// 예외 칸 옆 회색 기본값 — 이 달 계산에 쓴 기본 버전(스냅샷 달은 그 버전), 없으면 그 달에 적용되는 버전
const cardDefaultPlan = computed(() => {
  const version = rates.value?.default_version;
  const byVersion = version ? defaults.items.find((p) => p.effective_from === version) : null;
  return byVersion ?? effectiveDefaultPlan(defaults.items, props.month);
});

const ratesPageTo = computed(() => ({ name: SETTLEMENT_ROUTE_NAMES.rates, query: { month: props.month } }));

const planMissing = computed(() => {
  const legacy = legacyVersion.value;
  const parts = legacy
    ? ['기본 요율 화면에서 그 버전을 [수정]해 새 칸으로 다시 저장하세요.']
    : [`기본 요율 화면에서 적용 시작 월이 ${props.month} 이하인 버전을 만드세요.`];
  if (status.value === 'review') parts.push('그 뒤 정산 탭에서 [다시 계산]합니다.');
  // 경고 코드는 제목 대신 작은 회색 꼬리로(템플릿)
  return {
    message: legacy
      ? `${monthLabel(props.month)}에 적용되는 ${legacy} 버전이 v70 이전 모양이라 요율과 금액을 계산하지 않습니다.`
      : `${monthLabel(props.month)}에 적용할 기본 요율이 없어 요율과 금액을 계산하지 않습니다.`,
    code: 'DEFAULT_PLAN_MISSING',
    description: parts.join(' '),
  };
});

// ---------------------------------------------------------------------------
// 이 달에 쓰는 예외 버전
// ---------------------------------------------------------------------------

// 패널은 지금 목록 기준이다(요청 계약 3-3) — 추정 중 달의 계산, 검토 필요 달의 [다시 계산], 다음 마감이 쓸 버전.
// 카드(`rates`)는 스냅샷 달이면 마감 때 버전이라 다를 수 있다(아래 snapshotGapText). 목록을 못 받았으면 카드 기준.
const currentOverride = computed(() => (overrides.loaded ? effectiveOverride(overrides.items, props.month) : null));
const appliedKnown = computed(() => overrides.loaded || !!rates.value);
const appliedVersion = computed(() => {
  if (overrides.loaded) return currentOverride.value?.effective_from ?? null;
  return rates.value?.override_version ?? null;
});
const appliedCellCount = computed(() => {
  if (overrides.loaded) return overrideBodyCellKeys(currentOverride.value).length;
  return rates.value ? overriddenCellKeys(rates.value).length : 0;
});
const appliedSummary = computed(() => {
  if (!appliedKnown.value) return '';
  const version = appliedVersion.value;
  if (!version) return '이 달 적용 예외 없음';
  const n = appliedCellCount.value;
  // The version and the cell count are already in the card chip and the header — the pill only says it applies
  return n ? '이 달 적용 중' : '이 달 적용 중 · 덮은 칸 없음';
});

// 스냅샷 달인데 마감 때 쓴 예외 버전과 지금 버전이 다르면 카드와 패널이 왜 다른지 말한다
const snapshotGapText = computed(() => {
  if (!isSnapshot.value || !rates.value || !overrides.loaded) return '';
  const then = rates.value.override_version ?? null;
  if ((appliedVersion.value ?? null) === then) return '';
  const thenText = then ? `예외 ${then} 버전으로` : '예외 없이';
  return status.value === 'review'
    ? `위 표는 마감 시점 요율이라 ${thenText} 계산한 값입니다. 정산 탭에서 [다시 계산]하면 지금 버전이 들어갑니다.`
    : `확정된 정산은 마감 시점 요율(${thenText} 계산)을 그대로 씁니다.`;
});

// ---------------------------------------------------------------------------
// 카드 위 안내와 쓰기 뒤 할 일
// ---------------------------------------------------------------------------

/** 쓰기 뒤 남은 할 일 — 'recalculate'(검토 필요) | 'reopen'(확정) | null */
const followUp = ref(null);
// 할 일을 정한 시점의 상세 — 그 뒤 상세가 다시 오면(다시 계산·재수정 등) 할 일 안내를 거둔다
let followUpDetail = null;

watch(
  () => props.detail,
  (detail) => {
    if (followUp.value && detail !== followUpDetail) followUp.value = null;
  }
);

const statusNotice = computed(() => {
  if (followUp.value === 'recalculate' && status.value === 'review') {
    return {
      type: 'warning',
      message: '바꾼 예외는 정산 탭에서 [다시 계산]해야 이 달 정산에 들어갑니다.',
      action: 'recalculate',
    };
  }
  if (followUp.value === 'reopen' && status.value === 'confirmed') {
    return {
      type: 'info',
      message: '바꾼 예외는 확정된 이 달 금액을 바꾸지 않습니다. 넣으려면 재수정한 뒤 [다시 계산]합니다.',
      action: 'reopen',
    };
  }
  // v70 이전에 만든 정산서 — 카드는 그때의 요율(예전 모양)이라 칸별로 보이지 않는다(개정 8)
  if (legacyStatement.value && (status.value === 'review' || status.value === 'confirmed')) {
    return {
      type: 'warning',
      message: '요금표 v70 이전에 만든 정산서라 그때의 요율(예전 모양)입니다.',
      description: legacyStatementActionText(status.value),
      action: status.value === 'review' ? 'recalculate' : 'reopen',
    };
  }
  switch (status.value) {
    case 'review':
      return {
        type: 'info',
        message: '마감 시점 요율입니다. 요율을 고친 뒤 정산 탭에서 [다시 계산]해야 들어갑니다.',
        action: 'recalculate',
      };
    case 'confirmed':
      return {
        type: 'info',
        message: '확정된 달이라 마감 시점 요율입니다. 요율을 고쳐도 이 달은 바뀌지 않습니다.',
      };
    case 'estimating':
      return {
        type: 'info',
        message: '마감 전이라 요율을 고치면 이 표와 추정 금액이 바로 바뀝니다.',
      };
    case 'failed':
      return {
        type: 'info',
        message: '마감 실패라 요율을 고치면 이 표와 추정 금액이 바로 바뀌고, 다음 마감이 그 값으로 계산합니다.',
      };
    case 'closing':
      return { type: 'info', message: `${CLOSING_TEXT} 끝나면 이 표는 마감 시점 요율로 바뀝니다.` };
    default:
      return null;
  }
});

function runNoticeAction(action) {
  if (action === 'recalculate') {
    followUp.value = null;
    emit('go-tab', 'settle', { type: 'focus-recalculate' });
  } else if (action === 'reopen') {
    emit('open-reopen');
  }
}

/**
 * 이 버전이 이 달에 쓰이는(쓰이던) 버전인가 — 적용 시작 월이 이 달 이하이고, 그 뒤 이 달까지 다른 예외
 * 버전이 없다(예외 버전끼리는 쌓이지 않는다). 쓰기 전 목록으로 판정한다.
 */
function appliesToMonth(effectiveFrom) {
  if (!effectiveFrom || effectiveFrom > props.month) return false;
  return !overrides.items.some((o) => o.effective_from > effectiveFrom && o.effective_from <= props.month);
}

async function reloadDetail() {
  try {
    await props.reload();
  } catch {
    // 상세를 받지 못한 일은 셸이 알린다
  }
}

/**
 * 예외 쓰기 뒤 — 예외 목록과 상세를 다시 받고, 이 달에 무엇이 바뀌었는지 알린다.
 * @param {'saved'|'cleared'|'deleted'} kind
 * @param {string} effectiveFrom 쓴 버전
 * @param {{ quiet?: boolean }} [opts] quiet: 알림을 이미 띄웠다(이미 지워진 버전)
 */
async function afterWrite(kind, effectiveFrom, { quiet = false } = {}) {
  const affects = appliesToMonth(effectiveFrom);
  followUp.value = null;
  await Promise.all([loadOverrides(), reloadDetail()]);
  // 셸이 새 상세를 props 로 내려 준 뒤에 마감 상태를 읽는다
  await nextTick();
  const head = WRITE_DONE_TEXT[kind];
  if (!affects) {
    if (!quiet) message.success(`${head} ${monthLabel(props.month)}에 쓰는 버전이 아니라 이 달 정산은 그대로입니다.`);
    return;
  }
  const current = statementStatusOf(props.detail);
  if (current === 'review') followUp.value = 'recalculate';
  else if (current === 'confirmed') followUp.value = 'reopen';
  followUpDetail = props.detail;
  if (quiet) return;
  // 추정 중·마감 실패 달은 지금 요율로 다시 계산한 금액이 이미 왔다
  const live = current === 'estimating' || current === 'failed';
  message.success(live ? `${head} 이 달 추정 금액에 바로 반영했습니다.` : head);
}

// ---------------------------------------------------------------------------
// 예외 모달·이력
// ---------------------------------------------------------------------------

const modalOpen = ref(false);

function openModal() {
  if (plansReady.value) modalOpen.value = true;
}

function onSaved({ kind, effectiveFrom }) {
  modalOpen.value = false;
  void afterWrite(kind, effectiveFrom);
}

function onDeleted({ effectiveFrom, alreadyGone }) {
  void afterWrite('deleted', effectiveFrom, { quiet: alreadyGone });
}

// 정산 탭 2-4 의 "이 병원 예외로 넣기" → 목록을 받은 뒤 모달을 연다
let pendingIntentOpen = false;

function settlePendingIntent() {
  if (!pendingIntentOpen || (plansLoading.value && !plansReady.value)) return;
  pendingIntentOpen = false;
  if (plansReady.value) modalOpen.value = true;
  else message.warning('예외 이력과 기본 요율을 불러오지 못해 예외 모달을 열 수 없습니다. 회사 예외에서 다시 시도하세요.');
  emit('intent-done');
}

watch(
  () => props.intent?.nonce,
  () => {
    const intent = props.intent;
    if (!intent) return;
    if (intent.type !== 'open-override-modal') {
      emit('intent-done');
      return;
    }
    pendingIntentOpen = true;
    settlePendingIntent();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

// 카드 머리 슬롯의 `[예외 추가/수정]` 은 이 컴포넌트가 그린다
@include yellow-button;

.rates-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: $st-text;

  // 경고 코드 — 사람이 읽을 이름 뒤 작은 회색 꼬리
  &__code {
    margin-left: 6px;
    font-size: 11.5px;
    font-weight: 400;
    color: $st-muted;
  }

  &__link {
    margin-left: 4px;
    font-size: 12.5px;
    font-weight: 600;
    white-space: nowrap;
  }

  &__panel {
    @include st-card;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__panel-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
  }

  &__panel-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    color: $st-text;
  }

  &__pill {
    @include st-pill;

    &.is-override {
      @include st-pill-tone($st-override, $st-override-bg, $st-override-border);
    }
  }

  &__note {
    margin: 0;
    color: $st-text-secondary;
    font-size: 12.5px;
    line-height: 1.6;

    &.is-muted {
      color: $st-muted;
      font-size: 12px;
    }

    // 카드(마감 시점)와 지금 예외 버전이 다를 때
    &.is-gap {
      color: $st-warning;
    }
  }
}
</style>
